import type { CalculatorNotice } from "../../model/notices";
import { actionTargetKey, type NoticeTargets } from "./noticeTargets";

/** The wizard slides a step in over 0.3s (see LockCalculator.module.css); the target is reachable after it. */
export const STEP_TRANSITION_MS = 350;

/** Where focus lands inside a registered field: the design-system FormField gives no ref to its control. */
const FOCUSABLE = "input, textarea, button, [tabindex]";

const FLASH_FRAMES: Keyframe[] = [
  { borderRadius: "0.75rem", backgroundColor: "transparent", boxShadow: "0 0 0 0 rgba(245, 158, 11, 0)" },
  { borderRadius: "0.75rem", backgroundColor: "rgba(245, 158, 11, 0.22)", boxShadow: "0 0 0 6px rgba(245, 158, 11, 0.35)" },
  { borderRadius: "0.75rem", backgroundColor: "transparent", boxShadow: "0 0 0 0 rgba(245, 158, 11, 0)" },
];

/** A field's wrapper is display: contents, so the element that pulses is its first child. */
const flash = (element: HTMLElement) => {
  const reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
  element.animate(FLASH_FRAMES, reducedMotion ? { duration: 1200 } : { duration: 600, iterations: 3, easing: "ease-in-out" });
};

/** A group of fields is registered once under its name: "a.b" falls back to "a". */
const findField = (targets: NoticeTargets, path: string): HTMLElement | null => {
  const parent = path.includes(".") ? path.slice(0, path.lastIndexOf(".")) : null;
  return targets.find(path) ?? (parent ? findField(targets, parent) : null);
};

/**
 * Focuses and flashes what a notice points at: the calculation button that resolves it, else its first
 * field. Call it once the target's step is shown. Returns false when there is nothing to reach.
 */
export const focusNoticeTarget = (notice: CalculatorNotice, targets: NoticeTargets): boolean => {
  const path = notice.fields[0];
  const target = notice.action ? targets.find(actionTargetKey(notice.action)) : path ? findField(targets, path) : null;
  if (!target) return false;
  const control = notice.action ? target : target.querySelector<HTMLElement>(FOCUSABLE);
  control?.focus();
  control?.scrollIntoView({ block: "center", behavior: "smooth" });
  const flashed = notice.action ? target : target.firstElementChild;
  if (flashed instanceof HTMLElement) flash(flashed);
  return true;
};

/** Flashes a field without moving focus or scrolling: for a field a rule changed on its own. */
export const flashNoticeField = (path: string, targets: NoticeTargets): void => {
  const flashed = findField(targets, path)?.firstElementChild;
  if (flashed instanceof HTMLElement) flash(flashed);
};

/**
 * The notice with its first rendered field first. A field a rule changed may not be on screen (the lateral
 * resistance is only shown with a lateral load): "go to" then lands on the next field that is, the cause.
 */
export const withReachableField = (notice: CalculatorNotice, targets: NoticeTargets): CalculatorNotice => {
  if (notice.action) return notice;
  const index = notice.fields.findIndex((path) => findField(targets, path) !== null);
  const reachable = notice.fields[index];
  if (index <= 0 || reachable === undefined) return notice;
  return { ...notice, fields: [reachable, ...notice.fields.filter((_, position) => position !== index)] };
};
